// ==================== TOOLTIP COMPONENT ====================
// A reusable tooltip component that uses React Portal to ensure
// it's always visible above all other content with boundary detection

import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/helpers';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
type TooltipVariant = 'dark' | 'light' | 'orange';

interface TooltipProps {
  /** The content to show in the tooltip */
  content: ReactNode;
  /** The element that triggers the tooltip */
  children: ReactNode;
  /** Position of the tooltip relative to the trigger */
  position?: TooltipPosition;
  /** Visual variant of the tooltip */
  variant?: TooltipVariant;
  /** Delay before showing tooltip (ms) */
  delay?: number;
  /** Whether the tooltip is disabled */
  disabled?: boolean;
  /** Additional class names for the tooltip */
  className?: string;
  /** Show arrow pointer */
  showArrow?: boolean;
  /** Max width of tooltip */
  maxWidth?: number;
  /** Allow text to wrap */
  wrap?: boolean;
}

const Tooltip = ({
  content,
  children,
  position = 'right',
  variant = 'dark',
  delay = 0,
  disabled = false,
  className,
  showArrow = true,
  maxWidth = 250,
  wrap = true,
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [actualPosition, setActualPosition] = useState<TooltipPosition>(position);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const gap = 10; // Gap between trigger and tooltip
    const padding = 12; // Padding from viewport edges
    
    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Estimate tooltip dimensions (will be adjusted after render)
    const estimatedWidth = Math.min(maxWidth, 200);
    const estimatedHeight = 40;

    // Calculate available space in each direction
    const spaceTop = triggerRect.top - padding;
    const spaceBottom = viewportHeight - triggerRect.bottom - padding;
    const spaceLeft = triggerRect.left - padding;
    const spaceRight = viewportWidth - triggerRect.right - padding;

    // Determine best position based on available space
    let bestPosition = position;
    
    // Check if preferred position has enough space, otherwise flip
    switch (position) {
      case 'top':
        if (spaceTop < estimatedHeight && spaceBottom > spaceTop) {
          bestPosition = 'bottom';
        }
        break;
      case 'bottom':
        if (spaceBottom < estimatedHeight && spaceTop > spaceBottom) {
          bestPosition = 'top';
        }
        break;
      case 'left':
        if (spaceLeft < estimatedWidth && spaceRight > spaceLeft) {
          bestPosition = 'right';
        }
        break;
      case 'right':
        if (spaceRight < estimatedWidth && spaceLeft > spaceRight) {
          bestPosition = 'left';
        }
        break;
    }

    setActualPosition(bestPosition);

    // Calculate coordinates based on best position
    let top = 0;
    let left = 0;

    switch (bestPosition) {
      case 'top':
        top = triggerRect.top - gap;
        left = triggerRect.left + triggerRect.width / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + gap;
        left = triggerRect.left + triggerRect.width / 2;
        break;
      case 'left':
        top = triggerRect.top + triggerRect.height / 2;
        left = triggerRect.left - gap;
        break;
      case 'right':
      default:
        top = triggerRect.top + triggerRect.height / 2;
        left = triggerRect.right + gap;
        break;
    }

    // Clamp left position to stay within viewport for top/bottom positions
    if (bestPosition === 'top' || bestPosition === 'bottom') {
      const halfWidth = estimatedWidth / 2;
      if (left - halfWidth < padding) {
        left = padding + halfWidth;
      } else if (left + halfWidth > viewportWidth - padding) {
        left = viewportWidth - padding - halfWidth;
      }
    }

    // Clamp top position to stay within viewport for left/right positions
    if (bestPosition === 'left' || bestPosition === 'right') {
      const halfHeight = estimatedHeight / 2;
      if (top - halfHeight < padding) {
        top = padding + halfHeight;
      } else if (top + halfHeight > viewportHeight - padding) {
        top = viewportHeight - padding - halfHeight;
      }
    }

    setCoords({ top, left });
  }, [position, maxWidth]);

  // Recalculate position after tooltip renders to get actual dimensions
  useEffect(() => {
    if (isVisible && tooltipRef.current && triggerRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const padding = 12;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let { top, left } = coords;

      // Adjust if tooltip goes outside viewport
      if (actualPosition === 'top' || actualPosition === 'bottom') {
        // Check horizontal bounds
        if (left - tooltipRect.width / 2 < padding) {
          left = padding + tooltipRect.width / 2;
        } else if (left + tooltipRect.width / 2 > viewportWidth - padding) {
          left = viewportWidth - padding - tooltipRect.width / 2;
        }
      }

      if (actualPosition === 'left' || actualPosition === 'right') {
        // Check vertical bounds
        if (top - tooltipRect.height / 2 < padding) {
          top = padding + tooltipRect.height / 2;
        } else if (top + tooltipRect.height / 2 > viewportHeight - padding) {
          top = viewportHeight - padding - tooltipRect.height / 2;
        }
      }

      // Also check if tooltip would go off-screen in opposite direction
      if (actualPosition === 'top' && triggerRect.top - tooltipRect.height < padding) {
        // Not enough space at top, could flip to bottom but we already calculated best position
      }

      if (coords.top !== top || coords.left !== left) {
        setCoords({ top, left });
      }
    }
  }, [isVisible, coords, actualPosition]);

  const handleMouseEnter = () => {
    if (disabled) return;
    
    if (delay > 0) {
      timeoutRef.current = setTimeout(() => {
        calculatePosition();
        setIsVisible(true);
      }, delay);
    } else {
      calculatePosition();
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Variant styles
  const variantStyles = {
    dark: 'bg-gray-900 text-white border border-gray-700',
    light: 'bg-white text-gray-900 border border-gray-200 shadow-lg',
    orange: 'bg-orange-500 text-white border border-orange-400',
  };

  const arrowVariantStyles = {
    dark: 'bg-gray-900 border-l border-b border-gray-700',
    light: 'bg-white border-l border-b border-gray-200',
    orange: 'bg-orange-500 border-l border-b border-orange-400',
  };

  // Position-based transform and arrow position
  const positionStyles = {
    top: '-translate-x-1/2 -translate-y-full',
    bottom: '-translate-x-1/2',
    left: '-translate-x-full -translate-y-1/2',
    right: '-translate-y-1/2',
  };

  const arrowPositionStyles = {
    top: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rotate-[-135deg]',
    bottom: 'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45',
    left: 'right-0 top-1/2 -translate-y-1/2 translate-x-1/2 rotate-[135deg]',
    right: 'left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 rotate-[-45deg]',
  };

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <div
      ref={triggerRef}
      className="inline-flex"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      {children}
      {isVisible && content && createPortal(
        <div
          ref={tooltipRef}
          role="tooltip"
          className={cn(
            'fixed px-3 py-2 text-sm font-medium rounded-lg shadow-2xl pointer-events-none',
            'animate-in fade-in-0 zoom-in-95 duration-150',
            variantStyles[variant],
            positionStyles[actualPosition],
            className
          )}
          style={{
            zIndex: 99999,
            top: coords.top,
            left: coords.left,
            maxWidth: maxWidth,
          }}
        >
          <span className={wrap ? 'whitespace-normal break-words' : 'whitespace-nowrap'}>
            {content}
          </span>
          
          {/* Arrow */}
          {showArrow && (
            <div
              className={cn(
                'absolute w-2 h-2 rotate-45',
                arrowVariantStyles[variant],
                arrowPositionStyles[actualPosition]
              )}
            />
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export { Tooltip };
export type { TooltipProps, TooltipPosition, TooltipVariant };

